import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './MembershipPlans.css';

interface MembershipPlan {
  id: string;
  name: string;
  minimumDeposit: number;
  percentageDiscount: number;
  cashbackEnabled: boolean;
  cashbackPercentage: number;
  cashbackTiming: 'on_topup' | 'on_spend';
  priceMatching: boolean;
  pointsConversionRate: number;
  freeDelivery: boolean;
  status?: 'active' | 'inactive' | 'closed' | 'teaser';
}

const MembershipPlans: React.FC = () => {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<MembershipPlan | null | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [teaserPlan, setTeaserPlan] = useState<MembershipPlan | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistState, setWaitlistState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [waitlistMessage, setWaitlistMessage] = useState('');
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
  const [resendVerificationMessage, setResendVerificationMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const verificationPending = (location.state as { verificationPending?: boolean })?.verificationPending;
  const needsEmailVerification = Boolean(user && !user.isVerified);

  const sortedPlans = [...plans].sort((a, b) => a.minimumDeposit - b.minimumDeposit);
  const isEnrolled = currentPlan !== undefined && currentPlan !== null;
  const showContinueButton = !isEnrolled;
  const canSelectPlan = !needsEmailVerification;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/membership-plans');
        const nextPlans = response.data.plans || [];
        setPlans(nextPlans);
        if (!selectedPlanId && nextPlans.length) {
          const activePlan = nextPlans.find((plan: MembershipPlan) => plan.status === 'active');
          setSelectedPlanId(activePlan ? activePlan.id : null);
        }
      } catch (error) {
        console.error('Error fetching membership plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [selectedPlanId]);

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const response = await api.get('/users/me/plan');
        setCurrentPlan(response.data?.plan ?? null);
        setIsLoggedIn(true);
      } catch {
        setCurrentPlan(null);
        setIsLoggedIn(false);
      }
    };
    fetchCurrentPlan();
  }, []);

  const handleResendVerification = async () => {
    if (!user?.email) return;
    setResendVerificationMessage('');
    setResendVerificationLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: user.email });
      setResendVerificationMessage('Verification email sent. Check your inbox.');
    } catch (err: any) {
      setResendVerificationMessage(err.response?.data?.error || 'Failed to resend. Try again.');
    } finally {
      setResendVerificationLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedPlanId) return;
    setEnrollError('');
    const plan = sortedPlans.find((p) => p.id === selectedPlanId);
    if (plan?.status === 'teaser') return;
    if (isLoggedIn) {
      try {
        setEnrolling(true);
        await api.put('/users/me/plan', { planId: selectedPlanId });
        localStorage.setItem('selectedPlanId', selectedPlanId);
        navigate('/welcome');
      } catch (err: any) {
        setEnrollError(err.response?.data?.error || 'Could not enroll in plan. Please try again.');
      } finally {
        setEnrolling(false);
      }
    } else {
      localStorage.setItem('selectedPlanId', selectedPlanId);
      navigate('/welcome');
    }
  };

  const openTeaserModal = (plan: MembershipPlan) => {
    setTeaserPlan(plan);
    setWaitlistEmail('');
    setWaitlistMessage('');
    setWaitlistState('idle');
  };

  const closeTeaserModal = () => {
    setTeaserPlan(null);
    setWaitlistEmail('');
    setWaitlistMessage('');
    setWaitlistState('idle');
  };

  const handleJoinWaitlist = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!teaserPlan) return;
    if (!waitlistEmail.trim()) {
      setWaitlistState('error');
      setWaitlistMessage('Enter your email to join the waitlist.');
      return;
    }
    try {
      setWaitlistState('loading');
      setWaitlistMessage('');
      await api.post('/membership-plans/waitlist', {
        planId: teaserPlan.id,
        email: waitlistEmail.trim(),
      });
      setWaitlistState('success');
      setWaitlistMessage("You're on the waitlist! We'll be in touch soon.");
    } catch (error: any) {
      setWaitlistState('error');
      setWaitlistMessage(error.response?.data?.error || 'Unable to join waitlist.');
    }
  };

  return (
    <div className="container plans-page">
      <div className="plans-header">
        <h1>Membership Plan</h1>
        <p>Pick a plan that matches your top-up amount to unlock benefits.</p>
      </div>

      <div className="plans-note">
        All top-ups go directly into your shopping wallet and is available for grocery purchases.
      </div>
      {(verificationPending || needsEmailVerification) && (
        <div className="plans-verification plans-verification-required">
          <p className="plans-verification-title">
            {needsEmailVerification ? 'Verify your email to select a plan' : 'Check your email to verify your account before logging in.'}
          </p>
          {needsEmailVerification && (
            <p className="plans-verification-desc">
              We sent a verification link to <strong>{user?.email}</strong>. Click the link in that email, then refresh this page to choose a plan.
            </p>
          )}
          {needsEmailVerification && user?.email && (
            <div className="plans-verification-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleResendVerification}
                disabled={resendVerificationLoading}
              >
                {resendVerificationLoading ? 'Sending…' : 'Resend verification email'}
              </button>
              {resendVerificationMessage && (
                <span className="plans-verification-resend-msg">{resendVerificationMessage}</span>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading plans...</div>
      ) : sortedPlans.length ? (
        <div className="plans-grid">
          {sortedPlans.map((plan) => {
            const isTeaser = plan.status === 'teaser';
            return (
            <div
              key={plan.id}
              className={`plan-card ${selectedPlanId === plan.id ? 'selected' : ''} ${
                isTeaser ? 'teaser' : ''
              } ${!canSelectPlan && !isTeaser ? 'plan-card-disabled' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (isTeaser) {
                  openTeaserModal(plan);
                  return;
                }
                if (canSelectPlan) setSelectedPlanId(plan.id);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (isTeaser) {
                    openTeaserModal(plan);
                    return;
                  }
                  if (canSelectPlan) setSelectedPlanId(plan.id);
                }
              }}
              aria-pressed={selectedPlanId === plan.id}
              aria-label={`${isTeaser ? 'View' : 'Select'} ${plan.name} plan`}
            >
              <div className="plan-title-row">
                <h3>{plan.name}</h3>
                {isTeaser && <span className="plan-teaser-badge">Coming soon</span>}
              </div>
              <div className="plan-price">
                {plan.percentageDiscount}% member discount
              </div>
              <ul>
                <li>Minimum deposit: ${plan.minimumDeposit.toFixed(0)}</li>
                <li>Price matching: {plan.priceMatching ? 'Yes' : 'No'}</li>
                <li>
                  Cashback:{' '}
                  {plan.cashbackEnabled
                    ? `${plan.cashbackPercentage}% (${plan.cashbackTiming === 'on_spend' ? 'on spend' : 'on top-up'})`
                    : 'No'}
                </li>
                <li>Points conversion: {plan.pointsConversionRate}</li>
                <li>Free delivery: {plan.freeDelivery ? 'Yes' : 'No'}</li>
              </ul>
              {plan.cashbackEnabled &&
                plan.cashbackPercentage > 0 &&
                plan.percentageDiscount > 0 && (
                  <div className="plans-warning">
                    Cashback and discount are both enabled for this plan.
                  </div>
                )}
              {isTeaser && (
                <button
                  type="button"
                  className="btn btn-outline plan-teaser-action"
                  onClick={(event) => {
                    event.stopPropagation();
                    openTeaserModal(plan);
                  }}
                >
                  View details
                </button>
              )}
              {selectedPlanId === plan.id && !isTeaser && (
                <div className="plan-selected">Selected</div>
              )}
            </div>
          )})}
        </div>
      ) : (
        <div className="loading">No plan available yet.</div>
      )}

      {sortedPlans.length > 0 && (
        <div className="plans-compare">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowComparison(prev => !prev)}
          >
            {showComparison ? 'Hide comparison' : 'Compare plans'}
          </button>
          {showComparison && (
            <div className="plans-table">
              <div className="plans-table-row header">
                <div>Benefit</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>
                    {plan.name}
                    {plan.status === 'teaser' && <span className="plan-teaser-label">Coming soon</span>}
                  </div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Minimum deposit</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>${plan.minimumDeposit.toFixed(0)}</div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Member discount</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>{plan.percentageDiscount}%</div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Cashback</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>
                    {plan.cashbackEnabled
                      ? `${plan.cashbackPercentage}% (${plan.cashbackTiming === 'on_spend' ? 'spend' : 'top-up'})`
                      : 'No'}
                  </div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Free delivery</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>{plan.freeDelivery ? 'Yes' : 'No'}</div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Price matching</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>{plan.priceMatching ? 'Yes' : 'No'}</div>
                ))}
              </div>
              <div className="plans-table-row">
                <div>Points conversion</div>
                {sortedPlans.map(plan => (
                  <div key={plan.id}>{plan.pointsConversionRate}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="plans-footer">
        {showContinueButton && (
          <div className="plans-footer-continue">
            {enrollError && (
              <div className="plans-enroll-error" role="alert">
                {enrollError}
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleContinue}
              disabled={!selectedPlanId || enrolling || !canSelectPlan}
              title={!canSelectPlan ? 'Verify your email to select a plan' : undefined}
            >
              {enrolling ? 'Enrolling…' : 'Continue'}
            </button>
          </div>
        )}
        <Link to="/products" className="btn btn-outline">
          Browse Products
        </Link>
      </div>
      {teaserPlan && (
        <div className="plan-modal-overlay" role="dialog" aria-modal="true">
          <div className="plan-modal">
            <div className="plan-modal-header">
              <div>
                <h2>{teaserPlan.name}</h2>
                <p className="plan-modal-subtitle">Coming soon — join the waitlist</p>
              </div>
              <button type="button" className="plan-modal-close" onClick={closeTeaserModal}>
                ×
              </button>
            </div>
            <div className="plan-modal-body">
              <div className="plan-modal-summary">
                <div>
                  <span>Member discount</span>
                  <strong>{teaserPlan.percentageDiscount}%</strong>
                </div>
                <div>
                  <span>Minimum deposit</span>
                  <strong>${teaserPlan.minimumDeposit.toFixed(0)}</strong>
                </div>
              </div>
              <ul className="plan-modal-list">
                <li>Price matching: {teaserPlan.priceMatching ? 'Yes' : 'No'}</li>
                <li>
                  Cashback:{' '}
                  {teaserPlan.cashbackEnabled
                    ? `${teaserPlan.cashbackPercentage}% (${teaserPlan.cashbackTiming === 'on_spend' ? 'on spend' : 'on top-up'})`
                    : 'No'}
                </li>
                <li>Points conversion: {teaserPlan.pointsConversionRate}</li>
                <li>Free delivery: {teaserPlan.freeDelivery ? 'Yes' : 'No'}</li>
              </ul>
              <form className="plan-waitlist" onSubmit={handleJoinWaitlist}>
                <label>Join the waitlist</label>
                <div className="plan-waitlist-row">
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={waitlistEmail}
                    onChange={(event) => setWaitlistEmail(event.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={waitlistState === 'loading'}
                  >
                    {waitlistState === 'loading' ? 'Joining...' : 'Join waitlist'}
                  </button>
                </div>
                {waitlistMessage && (
                  <div className={`plan-waitlist-message ${waitlistState}`}>{waitlistMessage}</div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPlans;
